import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Bell, Check, CheckCheck, Loader2, Trash2 } from "lucide-react";

import { Button } from "../../components/ui";
import { usePartyContext } from "../../hooks/useParty";
import { trpc } from "../../lib/trpc";

export const Route = createFileRoute("/app/notifications")({
  staticData: { title: "Notifications" },
  component: NotificationsPage,
});

function NotificationsPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { setPendingInvite } = usePartyContext();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.notification.list.useInfiniteQuery(
      { limit: 20 },
      { getNextPageParam: (lastPage) => lastPage.nextCursor },
    );

  const markReadMutation = trpc.notification.markRead.useMutation({
    onSuccess: () => {
      void utils.notification.list.invalidate();
      void utils.notification.getUnreadCount.invalidate();
    },
  });

  const deleteMutation = trpc.notification.delete.useMutation({
    onSuccess: () => {
      void utils.notification.list.invalidate();
      void utils.notification.getUnreadCount.invalidate();
    },
  });

  const notifications = data?.pages.flatMap((p) => p.items) ?? [];

  const handleClick = (notification: (typeof notifications)[0]) => {
    // Mark as read
    if (!notification.isRead) {
      markReadMutation.mutate({ notificationId: notification.id });
    }

    // Navigate based on notification data
    // notification.data may be a JSON string (MariaDB returns json columns as strings)
    const raw = notification.data;
    let d: Record<string, unknown> | null = null;
    if (typeof raw === "string") {
      try {
        d = JSON.parse(raw);
      } catch {
        // malformed JSON — treat as no data
      }
    } else {
      d = raw as Record<string, unknown> | null;
    }
    // Party invite — show floating invite popup
    if (notification.type === "party_invite") {
      const partyId = typeof d?.partyId === "string" ? d.partyId : null;
      if (partyId) {
        setPendingInvite({
          partyId,
          inviterUsername:
            typeof d?.inviterUsername === "string"
              ? d.inviterUsername
              : notification.title.split(" invited ")[0] || "Someone",
          inviterAvatarUrl: typeof d?.inviterAvatarUrl === "string" ? d.inviterAvatarUrl : null,
          clubName: typeof d?.clubName === "string" ? d.clubName : null,
        });
        return;
      }
    }

    const clubSlug = typeof d?.clubSlug === "string" ? d.clubSlug : null;
    const disputeId = typeof d?.disputeId === "string" ? d.disputeId : null;
    if (clubSlug && disputeId) {
      void navigate({
        to: "/app/clubs/$slug/admin/disputes",
        params: { slug: clubSlug },
        search: {
          status: "all_unresolved",
          sort: "oldest",
          claim: "all",
          claimer: "all",
          dispute: disputeId,
        },
      });
      return;
    }
    if (clubSlug) {
      void navigate({ to: "/app/clubs/$slug", params: { slug: clubSlug } });
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="size-6" />
          Notifications
        </h1>
        {notifications.some((n) => !n.isRead) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markReadMutation.mutate({})}
            disabled={markReadMutation.isPending}
            leftIcon={<CheckCheck className="size-4" />}
          >
            Mark all read
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-surface-800 animate-pulse" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Bell className="size-10 text-surface-500 mb-3" />
          <p className="text-sm font-medium text-surface-300">No notifications yet</p>
          <p className="text-xs text-surface-500 mt-1">
            You'll be notified about matches, invites, and more
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleClick(notification)}
              className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                notification.isRead
                  ? "bg-surface-800/30 border-surface-700/30 hover:bg-surface-800/50"
                  : "bg-surface-800/60 border-brand-500/20 hover:bg-surface-800/80"
              }`}
            >
              <div
                className={`size-2 rounded-full mt-2 shrink-0 ${notification.isRead ? "bg-transparent" : "bg-brand-500"}`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">{notification.title}</p>
                {notification.body && (
                  <p className="text-xs text-surface-400 mt-0.5">{notification.body}</p>
                )}
                <p className="text-xs text-surface-500 mt-1">
                  {new Date(notification.createdAt).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!notification.isRead && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      markReadMutation.mutate({ notificationId: notification.id });
                    }}
                    className="p-1.5 rounded-md text-surface-500 hover:text-brand-400 hover:bg-brand-500/10 transition-colors"
                    title="Mark as read"
                  >
                    <Check className="size-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteMutation.mutate({ notificationId: notification.id });
                  }}
                  className="p-1.5 rounded-md text-surface-500 hover:text-error-400 hover:bg-error-500/10 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            </div>
          ))}

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                leftIcon={
                  isFetchingNextPage ? <Loader2 className="size-4 animate-spin" /> : undefined
                }
              >
                {isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
